import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Send,
  MessageSquare,
  HeadphonesIcon,
  Globe
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  const contactInfo = [
    {
      icon: <Mail className="h-6 w-6 text-emerald-600" />,
      title: 'Email Support',
      details: ['info@chartlinebrokerage.com', 'support@chartlinebrokerage.com'],
      description: 'Get in touch with our support team'
    },
    {
      icon: <Phone className="h-6 w-6 text-blue-600" />,
      title: 'Phone Support',
      details: ['+1 239 791 0799'],
      description: 'Call us for immediate assistance'
    },
    {
      icon: <MapPin className="h-6 w-6 text-purple-600" />,
      title: 'Office Location',
      details: ['2268 S Tongass Hwy', 'Ketchikan, Alaska(AK), 99901'],
      description: 'Visit our headquarters'
    },
    {
      icon: <Clock className="h-6 w-6 text-orange-600" />,
      title: 'Business Hours',
      details: ['Mon-Fri: 9AM-6PM EST', 'Sat: 10AM-4PM EST'],
      description: 'We\'re here when you need us'
    }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="contact-hero-content"
          >
            <div className="contact-hero-badge">
              <MessageSquare className="contact-hero-badge-icon" />
              <span>Get In Touch</span>
            </div>
            <h1 className="contact-hero-title">
              Contact
              <span className="contact-hero-title-gradient"> ChartLine</span>
            </h1>
            <p className="contact-hero-subtitle">
              Have questions? We're here to help. Reach out to our team for support, 
              inquiries, or to learn more about our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="contact-content-container">
          
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="contact-form-container"
          >
            <div className="contact-form-header">
              <h2 className="contact-form-title">Send us a Message</h2>
              <p className="contact-form-subtitle">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="contact-form-row">
                <div className="contact-form-group">
                  <label htmlFor="name" className="contact-form-label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="contact-form-input"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="contact-form-group">
                  <label htmlFor="email" className="contact-form-label">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="contact-form-input"
                    placeholder="Enter your email address"
                    required
                  />
                </div>
              </div>

              <div className="contact-form-group">
                <label htmlFor="subject" className="contact-form-label">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="contact-form-input"
                  placeholder="What is this about?"
                  required
                />
              </div>

              <div className="contact-form-group">
                <label htmlFor="message" className="contact-form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="contact-form-textarea"
                  placeholder="Tell us more about your inquiry..."
                  rows="6"
                  required
                ></textarea>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="contact-form-submit"
              >
                <Send className="contact-form-submit-icon" />
                Send Message
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="contact-info-container"
          >
            <div className="contact-info-header">
              <h2 className="contact-info-title">Contact Information</h2>
              <p className="contact-info-subtitle">
                Multiple ways to reach our team
              </p>
            </div>

            <div className="contact-info-grid">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="contact-info-card"
                >
                  <div className="contact-info-icon">
                    {info.icon}
                  </div>
                  <div className="contact-info-content">
                    <h3 className="contact-info-card-title">{info.title}</h3>
                    <p className="contact-info-card-description">{info.description}</p>
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="contact-info-detail">{detail}</p>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Live Chat CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="contact-live-chat"
            >
              <div className="contact-live-chat-content">
                <HeadphonesIcon className="contact-live-chat-icon" />
                <div className="contact-live-chat-text">
                  <h3 className="contact-live-chat-title">Need Immediate Help?</h3>
                  <p className="contact-live-chat-subtitle">Start a live chat with our support team</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="contact-live-chat-button"
                >
                  Start Chat
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="contact-map-section">
        <div className="contact-map-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="contact-map-content"
          >
            <h2 className="contact-map-title">Visit Our Office</h2>
            <p className="contact-map-subtitle">
              Located in the heart of New York's financial district
            </p>
            <div className="contact-map-placeholder">
              <Globe className="contact-map-icon" />
              <p>Interactive Map Coming Soon</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 